<template lang="pug">
Teleport(to="body")
  Transition(name="web-app-confirm")
    .web-app-confirm-backdrop(
      v-if="webAppConfirmState.visible"
      @click="cancelWebAppDialog"
      role="presentation"
    )
      .web-app-confirm-dialog(
        role="alertdialog"
        :aria-labelledby="titleId"
        :aria-describedby="messageId"
        @click.stop
      )
        button.web-app-confirm-close(
          type="button"
          aria-label="Close"
          @click="cancelWebAppDialog"
        )
          img(src="@/assets/icons/close.svg" alt="")

        .web-app-confirm-icon(
          :class="webAppConfirmState.variant === 'destructive' ? 'is-destructive' : 'is-default'"
          aria-hidden="true"
        )
          svg(
            v-if="webAppConfirmState.variant === 'destructive'"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          )
            polyline(points="3 6 5 6 21 6")
            path(d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2")
            line(x1="10" y1="11" x2="10" y2="17")
            line(x1="14" y1="11" x2="14" y2="17")
          svg(
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          )
            circle(cx="12" cy="12" r="10")
            line(x1="12" y1="16" x2="12" y2="12")
            line(x1="12" y1="8" x2="12.01" y2="8")

        h2.web-app-confirm-title(:id="titleId") {{ webAppConfirmState.title }}
        p.web-app-confirm-message(:id="messageId") {{ webAppConfirmState.message }}

        .web-app-confirm-actions
          button.web-app-confirm-btn.is-secondary(
            type="button"
            @click="cancelWebAppDialog"
          ) {{ webAppConfirmState.cancelText }}
          button.web-app-confirm-btn(
            type="button"
            :class="webAppConfirmState.variant === 'destructive' ? 'is-destructive' : 'is-primary'"
            @click="confirmWebAppDialog(true)"
          ) {{ webAppConfirmState.confirmText }}
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted } from 'vue';
import {
  webAppConfirmState,
  confirmWebAppDialog,
  cancelWebAppDialog,
} from '@/composables/useWebAppConfirm';

export default defineComponent({
  name: 'WebAppConfirmDialog',
  setup() {
    const titleId = 'web-app-confirm-title';
    const messageId = 'web-app-confirm-message';

    function onKeydown(event: KeyboardEvent) {
      if (!webAppConfirmState.visible) return;

      if (event.key === 'Escape') {
        cancelWebAppDialog();
      }
    }

    onMounted(() => {
      window.addEventListener('keydown', onKeydown);
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', onKeydown);
    });

    return {
      webAppConfirmState,
      confirmWebAppDialog,
      cancelWebAppDialog,
      titleId,
      messageId,
    };
  },
});
</script>

<style lang="scss">
.web-app-confirm-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.web-app-confirm-dialog {
  position: relative;
  width: min(100%, 400px);
  padding: 1.75rem 1.5rem 1.5rem;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
  text-align: center;
}

.web-app-confirm-close {
  position: absolute;
  top: 0.85rem;
  right: 0.85rem;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 999px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  img {
    width: 0.85rem;
    height: 0.85rem;
    opacity: 0.65;
  }

  &:hover {
    background: #e5e7eb;
  }
}

.web-app-confirm-icon {
  width: 3.25rem;
  height: 3.25rem;
  margin: 0 auto 1rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 1.35rem;
    height: 1.35rem;
  }

  &.is-destructive {
    background: #fff5f5;
    color: #c0392b;
  }

  &.is-default {
    background: #f0fdfa;
    color: #0d9488;
  }
}

.web-app-confirm-title {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.3;
}

.web-app-confirm-message {
  margin: 0 0 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #6b7280;
}

.web-app-confirm-actions {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.web-app-confirm-btn {
  width: 100%;
  border: none;
  border-radius: 999px;
  padding: 0.85rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:active {
    transform: scale(0.98);
  }

  &.is-primary {
    background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
    color: #fff;
    box-shadow: 0 8px 20px rgba(13, 148, 136, 0.28);

    &:hover {
      box-shadow: 0 10px 24px rgba(13, 148, 136, 0.34);
    }
  }

  &.is-destructive {
    background: #c0392b;
    color: #fff;
    box-shadow: 0 8px 20px rgba(192, 57, 43, 0.24);

    &:hover {
      background: #a93226;
    }
  }

  &.is-secondary {
    background: #f3f4f6;
    color: #374151;

    &:hover {
      background: #e5e7eb;
    }
  }
}

.web-app-confirm-enter-active,
.web-app-confirm-leave-active {
  transition: opacity 0.22s ease;

  .web-app-confirm-dialog {
    transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease;
  }
}

.web-app-confirm-enter-from,
.web-app-confirm-leave-to {
  opacity: 0;

  .web-app-confirm-dialog {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
}
</style>
